import { useEffect, useState } from "react";
import { useSearchParams, useNavigate, Link } from "react-router-dom";
import { CheckCircle2, XCircle, Loader2 } from "lucide-react";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";

export function PaymentSuccess() {
  const [sp] = useSearchParams();
  const nav = useNavigate();
  const sessionId = sp.get("session_id");
  const [state, setState] = useState("checking"); // checking | paid | pending | error

  useEffect(() => {
    if (!sessionId) { setState("error"); return; }
    let tries = 0;
    let active = true;
    const poll = async () => {
      try {
        const { data } = await api.get(`/payments/status/${sessionId}`);
        if (!active) return;
        if (data.payment_status === "paid") { setState("paid"); return; }
        if (["expired", "failed"].includes(data.payment_status)) { setState("error"); return; }
        if (tries++ < 8) setTimeout(poll, 2000); else setState("pending");
      } catch { if (active) setState("error"); }
    };
    poll();
    return () => { active = false; };
  }, [sessionId]);

  return (
    <div className="grid min-h-screen place-items-center p-6">
      <div className="w-full max-w-md rounded-2xl border border-border bg-card p-10 text-center shadow-sm">
        {state === "checking" && <><Loader2 className="mx-auto h-12 w-12 animate-spin text-primary" /><h1 className="mt-4 font-heading text-2xl font-bold">Confirming payment…</h1><p className="mt-2 text-muted-foreground">Please wait a moment.</p></>}
        {state === "paid" && <><CheckCircle2 className="mx-auto h-14 w-14 text-emerald-500" /><h1 className="mt-4 font-heading text-2xl font-bold">Application submitted!</h1><p className="mt-2 text-muted-foreground">Your application fee is paid. Track your status in your dashboard.</p><Button data-testid="payment-dashboard-btn" className="mt-6 w-full" onClick={() => nav("/dashboard")}>Go to my dashboard</Button></>}
        {state === "pending" && <><Loader2 className="mx-auto h-12 w-12 text-amber-500" /><h1 className="mt-4 font-heading text-2xl font-bold">Payment processing</h1><p className="mt-2 text-muted-foreground">This is taking longer than usual. Check your dashboard shortly.</p><Button className="mt-6 w-full" onClick={() => nav("/dashboard")}>Go to dashboard</Button></>}
        {state === "error" && <><XCircle className="mx-auto h-14 w-14 text-destructive" /><h1 className="mt-4 font-heading text-2xl font-bold">Couldn't confirm payment</h1><p className="mt-2 text-muted-foreground">Something went wrong. You can retry from your dashboard.</p><Button className="mt-6 w-full" onClick={() => nav("/dashboard")}>Go to dashboard</Button></>}
      </div>
    </div>
  );
}

export function PaymentCancel() {
  const nav = useNavigate();
  return (
    <div className="grid min-h-screen place-items-center p-6">
      <div className="w-full max-w-md rounded-2xl border border-border bg-card p-10 text-center shadow-sm">
        <XCircle className="mx-auto h-14 w-14 text-muted-foreground" />
        <h1 className="mt-4 font-heading text-2xl font-bold">Payment cancelled</h1>
        <p className="mt-2 text-muted-foreground">No charge was made. You can complete your application anytime.</p>
        <div className="mt-6 flex gap-3">
          <Button variant="outline" className="flex-1" onClick={() => nav("/courses")}>Browse courses</Button>
          <Link className="flex-1" to="/dashboard"><Button className="w-full">My dashboard</Button></Link>
        </div>
      </div>
    </div>
  );
}
